import { installTouchLoop, repeatBuffer } from './rr-featured-touch.js?v=20260918-native-loop-2';
export const wrapIndex = (index, count) => ((index % count) + count) % count;
export const slideEase = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
export const horizontalWheelIntent = e => Boolean(e.shiftKey && e.deltaY) || Math.abs(e.deltaX) > Math.abs(e.deltaY);

if (typeof customElements !== 'undefined' && !customElements.get('rr-featured-compare')) {
  const galleryResize = new ResizeObserver(entries => {
    const updates = entries.map(({ target }) => target.prepareResize());
    for (const update of updates) update?.();
    for (const { target } of entries) {
      if (target.pendingResizePosition === undefined) continue;
      target.position = target.pendingResizePosition;
      target.rebasedPosition = target.position;
      target.pendingResizePosition = undefined;
    }
    if (updates.some(Boolean)) window.RRDrawerLayout?.preserveScroll?.();
  });
  class FeaturedLoop extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.originals = [...this.children];
      this.index = this.originals.length > 1 ? 1 : 0;
      this.timer = null;
      this.frame = null;
      this.settle = null;
      this.visible = false;
      this.directionSign = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.resumeAt = 0;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.shell = this.closest('.floating-controls-container');
      this.block = this.closest('.featured-collections-carousel__item') || this.shell;
      const drawerSelector = '#facets-drawer, country-drawer, cart-drawer, header-sidebar, header-search';
      this.openDrawers = new Set(document.querySelectorAll(drawerSelector.split(', ').map(selector => selector + '[open]').join(', ')));
      const listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: this.abort.signal });
      listen(document, 'dialog:before-show', e => {
        if (!e.target.matches?.(drawerSelector)) return;
        this.openDrawers.add(e.target);
        this.cancel(); clearTimeout(this.timer);
      }, { capture: true });
      listen(document, 'dialog:after-hide', e => {
        if (e.target.hasAttribute?.('open')) return;
        this.openDrawers.delete(e.target);
        this.interact();
      }, { capture: true });
      for (const event of ['pointerdown', 'pointerup', 'focusin', 'keydown', 'click']) {
        listen(this.block, event, () => this.interact(), { passive: true });
      }
      listen(this.block, 'pointermove', e => {
        if (e.movementX || e.movementY) this.interact();
      }, { passive: true });
      listen(document, 'visibilitychange', () => { if (document.hidden) this.cancel(); this.schedule(); });
      listen(this.reduced, 'change', () => { this.cancel(); this.schedule(); });
      listen(this, 'scroll', () => {
        if (this.frame !== null || this.resizing) return;
        const rebased = this.rebasedPosition !== undefined && Math.abs(this.position - this.rebasedPosition) < 1;
        this.rebasedPosition = undefined;
        this.normalize();
        if (!rebased) {
          if (performance.now() < (this.ignoreWheelScrollUntil || 0)) this.ignoreWheelScrollUntil = performance.now() + 500;
          else this.interact();
        }
      }, { passive: true });
      listen(this, 'wheel', e => {
        if (horizontalWheelIntent(e)) {
          this.ignoreWheelScrollUntil = 0;
          this.cancel(); this.interact();
        } else {
          this.ignoreWheelScrollUntil = performance.now() + 500;
        }
      }, { passive: true });
      listen(this, 'pointerdown', e => {
        if (e.pointerType === 'touch' && window.innerWidth < 1000) return;
        this.ignoreWheelScrollUntil = 0;
        this.cancel(); this.drag = { x: e.clientX, left: this.scrollLeft, moved: false, mouse: e.pointerType === 'mouse' };
        this.interact();
      });
      listen(window, 'pointermove', e => {
        if (!this.drag?.mouse) return;
        const distance = e.clientX - this.drag.x;
        if (Math.abs(distance) > 5) this.drag.moved = true;
        if (this.drag.moved) { e.preventDefault(); this.scrollLeft = this.drag.left - distance; this.normalize(); this.interact(); }
      }, { passive: false });
      const release = () => { if (!this.drag || this.drag.touch) return; this.suppressClick = this.drag.moved; this.drag = null; this.normalize(); this.interact(); };
      listen(window, 'pointerup', release);
      listen(window, 'pointercancel', release);
      installTouchLoop(this);
      listen(this, 'dragstart', e => e.preventDefault());
      listen(this, 'click', e => {
        if (this.suppressClick) { e.preventDefault(); e.stopPropagation(); this.suppressClick = false; return; }
        if (!this.looping || e.detail === 0 || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const media = e.target.closest('.product-card__media');
        const card = media?.closest('product-card');
        if (!card || card.parentElement !== this) return;
        const viewport = document.documentElement.clientWidth;
        if (e.clientX >= viewport * 0.25 && e.clientX <= viewport * 0.75) return;
        e.preventDefault();
        e.stopPropagation();
        if (this.frame !== null) return;
        const target = [...this.children].indexOf(card);
        const current = Math.round((this.position + this.peek) / this.pitch);
        this.interact();
        this.move(target - current, true);
      }, { capture: true });
      galleryResize.observe(this);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.cancel();
        this.schedule();
      }, { threshold: 0.1 });
      this.intersection.observe(this);
      this.build();
    }

    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      galleryResize.unobserve(this); this.intersection?.disconnect();
      clearTimeout(this.resizeTimer); this.resizing = false;
      this.cancel(); clearTimeout(this.timer); clearTimeout(this.settle);
      this.querySelectorAll(':scope > [data-loop-copy]').forEach(e => e.remove());
      this.removeAttribute('data-loop-ready');
    }

    get sign() { return this.directionSign; }
    get position() { return this.scrollLeft * this.sign; }
    set position(value) { this.scrollLeft = value * this.sign; }

    prepareResize() {
      const viewport = this.clientWidth;
      if (!this.abort || viewport === this.lastWidth) return;
      const width = this.originals[0]?.getBoundingClientRect().width;
      if (!width) return;
      if (!this.resizing) this.resizePhase = this.looping ? (this.position + this.peek) / this.pitch - this.buffer : this.index;
      const model = this.resizeModel;
      const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
      const pitch = width + gap;
      const breakpoint = value => value < 700 ? 0 : value < 1000 ? 1 : 2;
      const fast = model && this.looping && this.originals.length * pitch > viewport + gap + 1;
      const controls = fast && breakpoint(model.viewport) !== breakpoint(window.innerWidth)
        ? this.prepareControls(true) : null;
      const buffer = Math.max(this.buffer || 0, this.requiredBuffer(viewport, pitch));
      return () => {
        this.resizing = true;
        this.cancel(); clearTimeout(this.timer); clearTimeout(this.resizeTimer);
        if (fast) {
          // Measurements for every gallery are complete before any DOM writes.
          this.ensureCopies(buffer);
          controls?.();
          this.pitch = pitch;
          this.lastWidth = viewport;
          this.peek = (viewport - width) / 2;
          this.pendingResizePosition = (this.buffer + this.resizePhase) * pitch - this.peek;
        } else this.build(this.resizePhase);
        this.resizeTimer = setTimeout(() => {
          this.resizing = false;
          this.normalize(); this.schedule();
        }, 120);
      };
    }

    copy(card, serial) {
      const clone = card.cloneNode(true);
      clone.dataset.loopCopy = '';
      clone.removeAttribute('reveal-on-scroll');
      clone.removeAttribute('data-shopify-editor-block');
      clone.style.opacity = '1'; clone.style.transform = 'none';
      clone.setAttribute('aria-hidden', 'true');
      const ids = new Map();
      for (const el of [clone, ...clone.querySelectorAll('[id]')]) {
        if (el.id) { const id = `${this.id}-repeat-${serial}-${el.id}`; ids.set(el.id, id); el.id = id; }
      }
      for (const el of clone.querySelectorAll('*')) {
        for (const attr of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'list']) {
          if (el.hasAttribute(attr)) el.setAttribute(attr, el.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
        }
        if (el.matches('a,button,input,select,textarea,[tabindex]')) el.setAttribute('tabindex', '-1');
        if (el.matches('img')) { el.loading = 'lazy'; el.decoding = 'async'; el.removeAttribute('fetchpriority'); }
      }
      return clone;
    }

    build(phase = this.index) {
      if (!this.originals.length || !this.clientWidth) return;
      const width = this.originals[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
      const pitch = width + gap;
      if (!width || (pitch === this.pitch && this.clientWidth === this.lastWidth)) return;
      if (this.resizeModel) {
        this.shell.style.removeProperty('margin-block-start');
        this.resizeModel.wrapper.style.removeProperty('top');
        for (const control of this.resizeModel.controls) control.style.removeProperty('top');
      }
      this.directionSign = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      const stack = this.closest('.section-stack');
      const heading = stack?.firstElementChild?.querySelector('p, h2');
      if (heading) {
        const style = getComputedStyle(heading);
        const headingWhitespace = (parseFloat(style.lineHeight) - parseFloat(style.fontSize) * 0.65) / 2;
        const stackGap = parseFloat(getComputedStyle(stack).rowGap) || 0;
        const visibleGap = width * (116 / 359) + headingWhitespace + stackGap;
        stack.style.setProperty('--rr-color-heading-overlap', `${visibleGap / 2}px`);
      }
      this.cancel();
      this.pitch = pitch; this.lastWidth = this.clientWidth;
      this.peek = (this.clientWidth - width) / 2;
      this.looping = this.originals.length * pitch > this.clientWidth + gap + 1;
      this.classList.toggle('is-scrollable', this.looping);
      this.shell.querySelectorAll('carousel-prev-button button,carousel-next-button button').forEach(b => { b.disabled = !this.looping; });
      if (!this.looping) {
        this.querySelectorAll(':scope > [data-loop-copy]').forEach(e => e.remove());
        this.removeAttribute('data-loop-ready');
        this.position = 0; this.alignControls(); this.schedule(); return;
      }
      const buffer = this.requiredBuffer(this.clientWidth, pitch);
      this.ensureCopies(buffer);
      this.position = (this.buffer + phase) * this.pitch - this.peek;
      this.rebasedPosition = this.position;
      this.alignControls();
      this.schedule();
    }

    requiredBuffer(viewport, pitch) {
      // Narrow desktop windows do not need the long native-touch runway.
      const screenWidth = navigator.maxTouchPoints > 0 ? window.innerWidth : Math.max(1000, window.innerWidth);
      return repeatBuffer(viewport, pitch, screenWidth);
    }

    ensureCopies(buffer) {
      if (this.buffer !== buffer || !this.hasAttribute('data-loop-ready')) {
        this.querySelectorAll(':scope > [data-loop-copy]').forEach(e => e.remove());
        this.buffer = buffer;
        const before = document.createDocumentFragment(), after = document.createDocumentFragment();
        for (let i = 0; i < this.buffer; i++) {
          before.append(this.copy(this.originals[wrapIndex(i - this.buffer, this.originals.length)], `before-${i}`));
          after.append(this.copy(this.originals[i % this.originals.length], `after-${i}`));
        }
        this.prepend(before); this.append(after);
        this.setAttribute('data-loop-ready', '');
      }
    }

    alignControls() {
      this.prepareControls()?.();
    }

    prepareControls(resizing = false) {
      if (!this.isConnected) return;
      const stack = this.closest('.section-stack');
      const wrapper = stack?.firstElementChild;
      const heading = wrapper?.querySelector('p, h2');
      const image = this.originals[0]?.querySelector('.product-card__image--primary');
      let shift = 0, overlap = 0;
      if (heading && image) {
        const style = getComputedStyle(heading);
        const rect = heading.getBoundingClientRect();
        const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        const text = style.textTransform === 'uppercase' ? heading.textContent.toUpperCase() : heading.textContent;
        const key = font + text;
        if (this.headingMetrics?.key !== key) {
          const context = document.createElement('canvas').getContext('2d');
          context.font = font;
          this.headingMetrics = { key, metrics: context.measureText(text.trim()) };
        }
        const { metrics } = this.headingMetrics;
        const inkBottom = rect.top + (rect.height + metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent) / 2 + metrics.actualBoundingBoxDescent;
        let lastInkBottom = inkBottom;
        let galleryGap = 10;
        const subtitle = wrapper.querySelector('h2');
        if (subtitle && subtitle !== heading) {
          const subtitleStyle = getComputedStyle(subtitle);
          const subtitleRect = subtitle.getBoundingClientRect();
          const subtitleFont = `${subtitleStyle.fontWeight} ${subtitleStyle.fontSize} ${subtitleStyle.fontFamily}`;
          const subtitleText = subtitleStyle.textTransform === 'uppercase' ? subtitle.textContent.toUpperCase() : subtitle.textContent;
          const subtitleKey = subtitleFont + subtitleText;
          if (this.subtitleMetrics?.key !== subtitleKey) {
            const context = document.createElement('canvas').getContext('2d');
            context.font = subtitleFont;
            this.subtitleMetrics = { key: subtitleKey, metrics: context.measureText(subtitleText.trim()) };
          }
          const subtitleMetrics = this.subtitleMetrics.metrics;
          const lineHeight = parseFloat(subtitleStyle.lineHeight);
          const baseline = (lineHeight + subtitleMetrics.fontBoundingBoxAscent - subtitleMetrics.fontBoundingBoxDescent) / 2;
          lastInkBottom = subtitleRect.bottom - lineHeight + baseline + subtitleMetrics.actualBoundingBoxDescent;
          galleryGap = 0;
        }
        const oldShift = parseFloat(getComputedStyle(wrapper).top) || 0;
        const currentOverlap = -(parseFloat(getComputedStyle(this.shell).marginBlockStart) || 0);
        const headingWhitespace = (parseFloat(style.lineHeight) - parseFloat(style.fontSize) * 0.65) / 2;
        const stackGap = parseFloat(getComputedStyle(stack).rowGap) || 0;
        overlap = resizing ? (this.originals[0].getBoundingClientRect().width * (116 / 359) + headingWhitespace + stackGap) / 2 : currentOverlap;
        shift = image.getBoundingClientRect().top - galleryGap - lastInkBottom + oldShift + currentOverlap - overlap;
      }
      const card = this.originals[0];
      const media = card?.querySelector('.product-card__media');
      if (!media) return;
      let center = media.offsetHeight / 2;
      for (let node = media; node && node !== this.shell; node = node.offsetParent) {
        center += node.offsetTop;
      }
      const width = card.getBoundingClientRect().width;
      const model = {
        viewport: window.innerWidth, width, stack, wrapper,
        controls: [...this.shell.querySelectorAll(':scope > .floating-controls-container__control')],
        gap: parseFloat(getComputedStyle(this).columnGap) || 0,
        overlap, shift,
        center, mediaRatio: media.getBoundingClientRect().height / width
      };
      const containerWidth = stack.clientWidth;
      const items = parseFloat(getComputedStyle(this).getPropertyValue('--product-list-items-per-row')) || 1;
      const responsive = (base, slope) => `calc(${base - containerWidth * slope}px + ${100 * slope}cqw)`;
      return () => {
        this.resizeModel = model;
        stack.style.setProperty('--rr-color-heading-overlap', `${overlap}px`);
        stack.style.setProperty('--rr-color-heading-shift', `${shift}px`);
        this.shell.style.setProperty('--rr-compare-image-center', `${center}px`);
        this.shell.style.marginBlockStart = responsive(-model.overlap, -58 / 359 / items);
        wrapper.style.top = responsive(model.shift, -58 / 359 / items);
        for (const control of model.controls) control.style.top = responsive(center, model.mediaRatio / 2 / items);
      };
    }

    normalize() {
      if (this.nativeTouchScrolling) return;
      if (!this.looping || this.frame !== null || this.resizing) return;
      const relative = this.position - (this.buffer * this.pitch - this.peek);
      const cycle = this.originals.length * this.pitch;
      const normalized = ((relative % cycle) + cycle) % cycle;
      if (Math.abs(relative - normalized) > 1) {
        const before = this.scrollLeft;
        this.position = this.buffer * this.pitch - this.peek + normalized;
        this.rebasedPosition = this.position;
        if (this.drag) this.drag.left += this.scrollLeft - before;
      }
      this.index = wrapIndex(Math.round(normalized / this.pitch), this.originals.length);
      if (this.dataset.loopIndex !== String(this.index)) this.dataset.loopIndex = this.index;
    }

    cancel() {
      this.stopTouchMomentum?.();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    previous() { this.interact(); this.move(-1, true); }
    next() { this.interact(); this.move(1, true); }

    move(step, fromArrow = false) {
      if (!this.looping || this.frame !== null || this.drag || this.resizing || (this.openDrawers.size && !fromArrow)) return;
      clearTimeout(this.settle); clearTimeout(this.timer); this.timer = null; this.normalize();
      const from = this.position;
      const current = Math.round((from + this.peek) / this.pitch);
      const target = (current + step) * this.pitch - this.peek;
      const duration = this.reduced.matches ? 0 : (fromArrow ? 1200 / 1.33 : 1200);
      const start = performance.now();
      const tick = now => {
        const progress = duration ? Math.min((now - start) / duration, 1) : 1;
        this.position = from + (target - from) * slideEase(progress);
        if (progress < 1) this.frame = requestAnimationFrame(tick);
        else { this.frame = null; this.normalize(); this.rebasedPosition = this.position; this.schedule(); }
      };
      this.frame = requestAnimationFrame(tick);
    }

    interact() {
      this.resumeAt = performance.now() + 5000;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer); this.timer = null;
      if (!this.abort || !this.visible || !this.looping || this.resizing || this.openDrawers.size || this.drag || this.frame !== null || this.reduced.matches || document.hidden || window.Shopify?.designMode) return;
      const remaining = this.resumeAt - performance.now();
      this.timer = setTimeout(() => { this.timer = null; this.move(1); }, Math.max(3000, remaining));
    }
  }
  customElements.define('rr-featured-compare', FeaturedLoop);
}
